/**
 * WorldObjectBridgesBuilder - renders authored polygon bridges from
 * `terrain_objects.json` (TerrainBridgeObject).
 *
 * Procedural mode: turns the polygon into an oriented bounding box and
 * builds a concrete deck via {@link createBridgeMesh}.
 * GLB mode: loads the model attached to the object type and instances it
 * at the polygon centroid, rotated by the OBB principal axis.
 */

import { loadGlb } from "../assets/GltfLoader";
import {
  sampleTerrainHeight,
  type TerrainAsset,
  type TerrainBridgeObject,
  type TerrainGlbPolygonObject,
} from "../assets/TerrainAsset";

type PolygonWorldObject = TerrainBridgeObject | TerrainGlbPolygonObject;

/**
 * Accepted shapes:
 *  - authored bridges (procedural deck) — must be polygon;
 *  - any object with render_mode === "glb" carrying either a polygon /
 *    line geometry or a precomputed `position` (point case).
 */
function isGlbOrBridgeWorldObject(o: unknown): o is PolygonWorldObject {
  if (typeof o !== "object" || o === null) return false;
  const rec = o as Record<string, unknown>;
  const geometry = rec.geometry as { type?: string } | undefined;
  if (rec.type === "bridge") {
    return geometry?.type === "polygon";
  }
  if (rec.render_mode !== "glb") return false;
  if (geometry?.type === "polygon" || geometry?.type === "line") return true;
  return Array.isArray(rec.position) && rec.position.length >= 2;
}
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Aabb } from "./Collisions";
import {
  bridgeAabbs,
  bridgeInstanceMatrix,
  createBridgeMesh,
  type BridgeFootprint,
} from "./AnnotationBridgesBuilder";

const GROUP_PREFIX = "worldobject.bridge";
const DEFAULT_DECK_HEIGHT_M = 3.5;

export class WorldObjectBridgesBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  async build(
    terrain: TerrainAsset,
    waterLevel: number,
    staticAabbs: Aabb[],
  ): Promise<number> {
    const candidates = ((terrain.worldObjects ?? []) as unknown[]).filter(
      isGlbOrBridgeWorldObject,
    );
    if (candidates.length === 0) return 0;

    let placed = 0;
    for (const obj of candidates) {
      const center = computePlacement(obj);
      if (!center) continue;
      const obb =
        obj.geometry?.type === "polygon"
          ? orientedBoundingBox(obj.geometry.points)
          : null;
      // Procedural bridge deck strictly needs a usable polygon OBB.
      if (obj.type === "bridge" && obj.render_mode !== "glb") {
        if (!obb || obb.length < 0.5 || obb.width < 0.3) continue;
      }

      const meshId = `${GROUP_PREFIX}.${obj.id}`;

      if (obj.render_mode === "glb" && obj.model) {
        // GLB carries its own authored orientation and pivot. Yaw comes purely
        // from Nova heading_deg (default 0). Height is locked to water level.
        const yaw =
          obj.headingY ??
          (obj.heading_deg !== undefined
            ? (obj.heading_deg * Math.PI) / 180
            : 0);
        const cx = center[0] + (obj.offset_x_m ?? 0);
        const cz = center[1] + (obj.offset_z_m ?? 0);
        const ok = await this.placeGlb(
          meshId,
          terrain,
          obj,
          cx,
          cz,
          yaw,
          waterLevel,
          staticAabbs,
        );
        if (ok) placed++;
        continue;
      }

      // Procedural deck path only applies to authored bridges.
      if (obj.type !== "bridge" || !obb) continue;
      const bridgeObj = obj as TerrainBridgeObject;
      const yawOffsetRad =
        bridgeObj.heading_deg !== undefined
          ? (bridgeObj.heading_deg * Math.PI) / 180
          : 0;
      const yaw = bridgeObj.headingY ?? obb.yaw + yawOffsetRad;
      const footprint = makeFootprint(bridgeObj, terrain, obb, yaw);
      this.staticMeshPass.addGroup(
        meshId,
        createBridgeMesh(footprint, meshId),
        0,
        { castsShadow: true, receivesShadow: true },
      );
      this.staticMeshPass.addInstance(
        meshId,
        bridgeInstanceMatrix(footprint, waterLevel),
      );
      staticAabbs.push(...bridgeAabbs(footprint, waterLevel));
      placed++;
    }
    if (placed > 0) {
      console.log(
        `[WorldObjectBridges] rendered ${placed} polygon GLB / bridge object(s)`,
      );
    }
    return placed;
  }

  private async placeGlb(
    meshId: string,
    terrain: TerrainAsset,
    obj: PolygonWorldObject,
    cx: number,
    cz: number,
    yaw: number,
    waterLevel: number,
    staticAabbs: Aabb[],
  ): Promise<boolean> {
    const url = resolveModelUrl(terrain, obj.model!);
    try {
      const mesh = await loadGlb(url);
      this.staticMeshPass.addGroup(meshId, mesh, 0, {
        castsShadow: true,
        castsHardShadow: true,
        receivesShadow: true,
      });
      const y = obj.height_m ?? 0;
      this.staticMeshPass.addInstance(meshId, makeYawMatrix(cx, y, cz, yaw));
      if (obj.collision !== false) {
        if (mesh.collisionBoxes && mesh.collisionBoxes.length > 0) {
          // Authored compound collider (Unreal-style `UBX_*` nodes in
          // Blender). Each box already maps a piece of the model in
          // local space; we just rotate + translate it to world. No
          // heuristics, no clearance / supports fields needed.
          const kind: Aabb["kind"] =
            obj.type === "bridge" ? "bridge" : "obstacle";
          for (const cb of mesh.collisionBoxes) {
            const xz = rotatedXZAabb(
              cb.min[0],
              cb.max[0],
              cb.min[2],
              cb.max[2],
              cx,
              cz,
              yaw,
            );
            staticAabbs.push({
              min: [xz.minX, y + cb.min[1], xz.minZ],
              max: [xz.maxX, y + cb.max[1], xz.maxZ],
              kind,
            });
          }
        } else {
          staticAabbs.push(
            ...worldAabbsForGlb(mesh.bbox, cx, y, cz, yaw, obj, waterLevel),
          );
        }
      }
      return true;
    } catch (err) {
      console.warn(
        `[WorldObjectBridges] failed to load GLB "${url}" for ${obj.id}: ${(err as Error).message}`,
      );
      return false;
    }
  }
}

/**
 * Build world-space AABB collider(s) from a GLB's local bbox, the yaw
 * rotation around +Y and the (cx, y, cz) translation used to place the
 * instance.
 *
 * Three cases:
 *   1. No clearance hint  — single AABB covering the full bbox. Walls,
 *      pillars, fishing platforms etc. collide from the waterline up.
 *   2. `clearance_m` (or `height_m` for bridges) declared  — generates
 *      THREE AABBs: an overhead deck/pipe slab above the sill plus two
 *      side supports at the ends of the model's long axis going from
 *      the waterline up to the sill. So a boat with the mast down can
 *      sail under a bridge / pipe / gantry, but the hull (or the mast
 *      tip when veering) still hits the supports on either side.
 *      Authors can opt out of the auto-generated supports by setting
 *      `supports: false` for genuinely floating obstacles.
 */
function worldAabbsForGlb(
  bbox: {
    min: readonly [number, number, number];
    max: readonly [number, number, number];
  },
  cx: number,
  y: number,
  cz: number,
  yaw: number,
  obj: PolygonWorldObject,
  waterLevel: number,
): Aabb[] {
  const explicitClearance = obj.clearance_m;
  const bridgeFallback =
    obj.type === "bridge" ? (obj.height_m ?? DEFAULT_DECK_HEIGHT_M) : undefined;
  const clearance =
    explicitClearance !== undefined ? explicitClearance : bridgeFallback;
  const kind: Aabb["kind"] = obj.type === "bridge" ? "bridge" : "obstacle";

  const fullMinY = y + bbox.min[1];
  const fullMaxY = y + bbox.max[1];
  const fullXZ = rotatedXZAabb(
    bbox.min[0],
    bbox.max[0],
    bbox.min[2],
    bbox.max[2],
    cx,
    cz,
    yaw,
  );

  if (clearance === undefined) {
    return [
      {
        min: [fullXZ.minX, fullMinY, fullXZ.minZ],
        max: [fullXZ.maxX, fullMaxY, fullXZ.maxZ],
        kind,
      },
    ];
  }

  const sill = waterLevel + clearance;
  const out: Aabb[] = [];
  if (fullMaxY > sill + 0.01) {
    out.push({
      min: [fullXZ.minX, sill, fullXZ.minZ],
      max: [fullXZ.maxX, fullMaxY, fullXZ.maxZ],
      kind,
    });
  }

  // Side supports along the model's long axis. Authors can disable
  // for truly floating obstacles.
  if (obj.supports !== false) {
    const dxLen = bbox.max[0] - bbox.min[0];
    const dzLen = bbox.max[2] - bbox.min[2];
    const longAxis: "x" | "z" = dxLen >= dzLen ? "x" : "z";
    const longExtent = longAxis === "x" ? dxLen : dzLen;
    const supportThickness = Math.max(
      0.6,
      Math.min(longExtent * 0.5, longExtent * 0.12, 2.0),
    );
    const supportFloor = waterLevel - 0.2;
    if (sill > supportFloor + 0.05) {
      const sides: Array<{ lo: number; hi: number }> = [];
      if (longAxis === "x") {
        sides.push({
          lo: bbox.min[0],
          hi: bbox.min[0] + supportThickness,
        });
        sides.push({
          lo: bbox.max[0] - supportThickness,
          hi: bbox.max[0],
        });
        for (const s of sides) {
          const xz = rotatedXZAabb(
            s.lo,
            s.hi,
            bbox.min[2],
            bbox.max[2],
            cx,
            cz,
            yaw,
          );
          out.push({
            min: [xz.minX, supportFloor, xz.minZ],
            max: [xz.maxX, sill, xz.maxZ],
            kind,
          });
        }
      } else {
        sides.push({
          lo: bbox.min[2],
          hi: bbox.min[2] + supportThickness,
        });
        sides.push({
          lo: bbox.max[2] - supportThickness,
          hi: bbox.max[2],
        });
        for (const s of sides) {
          const xz = rotatedXZAabb(
            bbox.min[0],
            bbox.max[0],
            s.lo,
            s.hi,
            cx,
            cz,
            yaw,
          );
          out.push({
            min: [xz.minX, supportFloor, xz.minZ],
            max: [xz.maxX, sill, xz.maxZ],
            kind,
          });
        }
      }
    }
  }

  return out;
}

/**
 * World-space axis-aligned XZ bounds of an oriented rectangle defined
 * by local x in [lx0, lx1] and local z in [lz0, lz1], rotated by yaw
 * around +Y and translated to (cx, cz). Conservative for non-axis-
 * aligned bridges — the world AABB is the smallest enclosing AABB of
 * the rotated rectangle, which is what the collision SAT expects.
 */
function rotatedXZAabb(
  lx0: number,
  lx1: number,
  lz0: number,
  lz1: number,
  cx: number,
  cz: number,
  yaw: number,
): { minX: number; maxX: number; minZ: number; maxZ: number } {
  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const lx of [lx0, lx1]) {
    for (const lz of [lz0, lz1]) {
      const wx = cx + cosY * lx - sinY * lz;
      const wz = cz + sinY * lx + cosY * lz;
      if (wx < minX) minX = wx;
      if (wx > maxX) maxX = wx;
      if (wz < minZ) minZ = wz;
      if (wz > maxZ) maxZ = wz;
    }
  }
  return { minX, maxX, minZ, maxZ };
}

function resolveModelUrl(terrain: TerrainAsset, model: string): string {
  if (/^(https?:)?\/\//.test(model) || model.startsWith("/")) return model;
  const base = terrain.baseUrl.endsWith("/")
    ? terrain.baseUrl
    : `${terrain.baseUrl}/`;
  return `${base}${model.replace(/^\/+/, "")}`;
}

function makeYawMatrix(x: number, y: number, z: number, yaw: number): Mat4 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  // prettier-ignore
  return new Float32Array([
    c,  0, s, 0,
    0,  1, 0, 0,
    -s, 0, c, 0,
    x,  y, z, 1,
  ]) as Mat4;
}

function makeFootprint(
  obj: TerrainBridgeObject,
  terrain: TerrainAsset,
  obb: Obb,
  yaw: number,
): BridgeFootprint {
  const deckOffset = obj.height_m ?? DEFAULT_DECK_HEIGHT_M;
  const cx = obb.center[0];
  const cz = obb.center[1];
  const halfL = obb.length * 0.5;
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const minusEndX = cx + cosYaw * -halfL;
  const minusEndZ = cz + sinYaw * -halfL;
  const plusEndX = cx + cosYaw * halfL;
  const plusEndZ = cz + sinYaw * halfL;
  const terrainMinus = sampleTerrainHeight(terrain, minusEndX, minusEndZ);
  const terrainPlus = sampleTerrainHeight(terrain, plusEndX, plusEndZ);
  const deckBaseMinusY = Math.max(deckOffset, terrainMinus + 0.2);
  const deckBasePlusY = Math.max(deckOffset, terrainPlus + 0.2);
  return {
    x: cx,
    z: cz,
    length: obb.length,
    width: obb.width,
    roadWidth: Math.max(2.8, obb.width - 0.5),
    yaw,
    pixelCount: 0,
    deckBaseY: (deckBaseMinusY + deckBasePlusY) * 0.5,
    deckBaseMinusY,
    deckBasePlusY,
    terrainMinusY: terrainMinus,
    terrainPlusY: terrainPlus,
  };
}

interface Obb {
  center: [number, number];
  length: number;
  width: number;
  yaw: number;
}

function orientedBoundingBox(
  points: ReadonlyArray<[number, number]>,
): Obb | null {
  if (points.length < 2) return null;
  let bestLen = 0;
  let axisX = 1;
  let axisZ = 0;
  for (let i = 0; i < points.length; i++) {
    const [ax, az] = points[i];
    const [bx, bz] = points[(i + 1) % points.length];
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len > bestLen) {
      bestLen = len;
      axisX = dx / len;
      axisZ = dz / len;
    }
  }
  if (bestLen <= 0) return null;

  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const [px, pz] of points) {
    const u = px * axisX + pz * axisZ;
    const v = -px * axisZ + pz * axisX;
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }

  const cu = (uMin + uMax) * 0.5;
  const cv = (vMin + vMax) * 0.5;
  const cx = cu * axisX - cv * axisZ;
  const cz = cu * axisZ + cv * axisX;

  return {
    center: [cx, cz],
    length: uMax - uMin,
    width: vMax - vMin,
    yaw: Math.atan2(axisZ, axisX),
  };
}

function polygonCentroid(
  points: ReadonlyArray<[number, number]>,
): [number, number] {
  const n = points.length;
  if (n === 0) return [0, 0];
  if (n < 3) {
    let sx = 0;
    let sz = 0;
    for (const [px, pz] of points) {
      sx += px;
      sz += pz;
    }
    return [sx / n, sz / n];
  }
  let area2 = 0;
  let cx = 0;
  let cz = 0;
  for (let i = 0; i < n; i++) {
    const [x0, z0] = points[i];
    const [x1, z1] = points[(i + 1) % n];
    const cross = x0 * z1 - x1 * z0;
    area2 += cross;
    cx += (x0 + x1) * cross;
    cz += (z0 + z1) * cross;
  }
  if (Math.abs(area2) < 1e-9) {
    let sx = 0;
    let sz = 0;
    for (const [px, pz] of points) {
      sx += px;
      sz += pz;
    }
    return [sx / n, sz / n];
  }
  const factor = 1 / (3 * area2);
  return [cx * factor, cz * factor];
}

/** Midpoint along the cumulative arc-length of an open polyline. */
function lineMidpoint(
  points: ReadonlyArray<[number, number]>,
): [number, number] {
  const n = points.length;
  if (n === 0) return [0, 0];
  if (n === 1) return [points[0][0], points[0][1]];
  let total = 0;
  for (let i = 1; i < n; i++) {
    const dx = points[i][0] - points[i - 1][0];
    const dz = points[i][1] - points[i - 1][1];
    total += Math.hypot(dx, dz);
  }
  if (total <= 1e-9) {
    return [points[0][0], points[0][1]];
  }
  const target = total * 0.5;
  let acc = 0;
  for (let i = 1; i < n; i++) {
    const ax = points[i - 1][0];
    const az = points[i - 1][1];
    const bx = points[i][0];
    const bz = points[i][1];
    const segLen = Math.hypot(bx - ax, bz - az);
    if (acc + segLen >= target) {
      const t = segLen > 1e-9 ? (target - acc) / segLen : 0;
      return [ax + (bx - ax) * t, az + (bz - az) * t];
    }
    acc += segLen;
  }
  return [points[n - 1][0], points[n - 1][1]];
}

/**
 * Resolve the placement (cx, cz) for a world object in world metres.
 * - point: explicit `position`;
 * - line: midpoint of the polyline (or explicit `position` if baked);
 * - polygon: polygon centroid (or explicit `position` if baked).
 * Authoring offsets (`offset_x_m`, `offset_z_m`) are NOT applied here —
 * the caller adds them so the procedural-bridge path can choose to use
 * a different reference point.
 */
function computePlacement(obj: PolygonWorldObject): [number, number] | null {
  const pos = (obj as { position?: [number, number] }).position;
  if (Array.isArray(pos) && pos.length >= 2) {
    return [pos[0], pos[1]];
  }
  const geom = obj.geometry;
  if (geom?.type === "polygon") return polygonCentroid(geom.points);
  if (geom?.type === "line") return lineMidpoint(geom.points);
  return null;
}
